/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useMemo, useEffect, HTMLAttributes } from 'react';
import styled from 'styled-components';
import { isEmpty, toNumber, uniqBy } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';
import { getCryptoIconPath } from '../../utils/portfolio';
import { MIN_TOKEN_SYMBOL } from '../../utils/consts';
import { sendSentryError } from '../../utils/sentry';
import { useAlertModal, usePortfolioDeposit } from '../../hooks';
import {
  PortfolioPartitionAction,
  useDepositCurrenciesOptionsQuery,
  type CurrencyInput,
} from '../../apollo';
import { useExchange, useAlert } from '../../providers';
import { Spinner } from '../../styled';
import { BlockTitle, BlockText as DefBlockText } from '../../styled/portfolio';
import {
  PortfolioManipulationForm,
  type PortfolioManipulationFormCurrenciesOption,
  type PortfolioManipulationFormProps,
} from '../forms';

type IndexPortfolioDepositProps = HTMLAttributes<HTMLDivElement>;

const IndexPortfolioDeposit = (props: IndexPortfolioDepositProps) => {
  const { t } = useTranslation();
  const { exchange } = useExchange();
  const { setAlert } = useAlert();
  const { dispatch: dispatchAlertModal } = useAlertModal();

  const { loading, error, data } = useDepositCurrenciesOptionsQuery({
    fetchPolicy: 'no-cache',
    skip: !exchange?.exchangeID,
    pollInterval: 10 * 1000,
    variables: {
      exchangeId: toNumber(exchange?.exchangeID),
    },
  });

  const { loading: loadingCurrenciesOptions, value: currenciesOptions = [] } =
    useAsync(async () => {
      const options: Array<PortfolioManipulationFormCurrenciesOption> =
        await Promise.all(
          [...(data?.getDepositCurrenciesOptions ?? [])].map(
            async ({ currency: { id, symbol }, cost }) => {
              const minToken = symbol === MIN_TOKEN_SYMBOL;
              const label = minToken
                ? t('INDEX_PORTFOLIO_DEPOSIT__minTokensLabel')
                : symbol.toUpperCase();
              const icon = !minToken ? await getCryptoIconPath(symbol) : false;
              return {
                label,
                value: id,
                icon,
                cost,
                semiBoldLabel: minToken,
              };
            }
          )
        );

      return uniqBy(options, 'value');
    }, [data]);

  const formDisabled = useMemo(() => exchange?.sentToRebalancing, [exchange]);

  const [deposit] = usePortfolioDeposit({
    fetchPolicy: 'no-cache',
    onCompleted: () => {
      dispatchAlertModal(['setModalContent', 'pendingExecRebalance']);
    },
  });

  useEffect(() => {
    const noExchangeIdError = !exchange?.exchangeID
      ? new Error('No exchange id error in IndexPortfolio deposit')
      : undefined;
    const finalError = noExchangeIdError || error;

    if (finalError) {
      sendSentryError(finalError);
    }
  }, [exchange?.exchangeID, error]);

  const onSubmit: PortfolioManipulationFormProps['onSubmit'] = async (
    values,
    { resetForm }
  ) => {
    try {
      if (!exchange?.exchangeID) {
        throw new Error('No exchange id in deposit mutation');
      }

      const isSingle = values.currenciesIds.length === 1;

      const currencies: CurrencyInput[] = isSingle
        ? [
            {
              id: toNumber(values.currenciesIds[0]),
              percentage: toNumber(values.percent),
            },
          ]
        : values.currenciesIds.map((id) => ({ id: toNumber(id) }));

      await deposit({
        variables: {
          exchangeId: toNumber(exchange.exchangeID),
          currencies,
        },
      });

      resetForm();
    } catch (e) {
      sendSentryError(e);
      setAlert({
        type: 'error',
        message: t('COMMON_ERROR'),
      });
    }
  };

  return (
    <Wrapper {...props}>
      <BlockTitle>{t('INDEX_PORTFOLIO_DEPOSIT_DESC__title')}</BlockTitle>
      <BlockText>{t('INDEX_PORTFOLIO_DEPOSIT_DESC__text1')}</BlockText>
      {(loading || loadingCurrenciesOptions) && isEmpty(currenciesOptions) ? (
        <Spinner
          style={{
            margin: '20px auto',
          }}
        />
      ) : (
        <FormWrapper>
          <BlockText weight={'semiBold'}>
            {t('INDEX_PORTFOLIO_DEPOSIT__formTitle')}
          </BlockText>
          <PortfolioManipulationForm
            portfolioManipulationAction={PortfolioPartitionAction.Deposit}
            multiple
            withSelectAll
            options={currenciesOptions}
            inputsPrefix={'deposit'}
            disabled={formDisabled}
            buttonText={'INDEX_PORTFOLIO_DEPOSIT_DESC__buttonText'}
            onSubmit={onSubmit}
          />
        </FormWrapper>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.section`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const FormWrapper = styled.div`
  margin-top: auto;
`;

const BlockText = styled(DefBlockText)`
  margin-bottom: 16px;
`;

export { IndexPortfolioDeposit };
